import { Controller } from "@hotwired/stimulus"
import { getRegionsForCountry, getPlaceholderForCountry } from "../utils/region_data"

// Dynamically updates region options based on selected country.
// Falls back to a text input when no region data exists for the selected country.
// Usage:
// <div data-controller="region-select"
//      data-region-select-country-select-name-value="form[country_code]">
//   <select data-region-select-target="select">...</select>
//   <input type="text" data-region-select-target="input" disabled hidden>
// </div>
export default class extends Controller {
  static targets = ["select", "input"]
  static values = {
    countrySelectName: { type: String, default: "form[country_code]" },
    selected: String
  }

  connect() {
    this.boundHandleCountryChange = this.handleCountryChange.bind(this)
    this.countryElement = this.findCountryElement();

    if (this.countryElement) {
      // Listen for HotwireCombobox selection events (bubbles from combobox fieldset)
      const comboboxFieldset = this.countryElement.closest("fieldset.hw-combobox")
      if (comboboxFieldset) {
        this.comboboxFieldset = comboboxFieldset
        comboboxFieldset.addEventListener("hw-combobox:selection", this.boundHandleCountryChange)
      } else {
        // Fallback for native select
        this.countryElement.addEventListener("change", this.boundHandleCountryChange)
      }
    }

    // If a country is already selected, load regions
    if (this.countryElement && this.countryElement.value) {
      this.loadRegions(this.countryElement.value)
    } else {
      this.disableSelect()
    }
  }

  disconnect() {
    if (this.comboboxFieldset) {
      this.comboboxFieldset.removeEventListener("hw-combobox:selection", this.boundHandleCountryChange)
    } else if (this.countryElement) {
      this.countryElement.removeEventListener("change", this.boundHandleCountryChange)
    }
  }

  handleCountryChange(event) {
    // For combobox events, get value from event detail
    const countryCode = event.detail?.value || this.countryElement.value

    if (countryCode === this.currentCountryCode) {
      return
    }

    if (!countryCode) {
      this.clearOptions()
      this.disableSelect()
      this.hideInput()
      return
    }

    this.loadRegions(countryCode)
  }

  loadRegions(countryCode) {
    this.currentCountryCode = countryCode
    const regions = getRegionsForCountry(countryCode)

    if (regions.length === 0) {
      this.showInput()
    } else {
      this.hideInput()
      const placeholder = getPlaceholderForCountry(countryCode)
      this.updateOptions({ regions, placeholder })
      this.enableSelect()
    }
  }

  updateOptions({ regions, placeholder }) {
    const _placeholder = placeholder || "Select state..."
    const selected = this.selectedValue || ""
    const required = this.selectTarget.hasAttribute("required")

    let html = ""

    if (_placeholder) {
      const placeholderSelected = !selected ? "selected" : ""
      html += `<option value="" ${placeholderSelected} ${required ? "disabled" : ""}>${this.escapeHtml(_placeholder)}</option>`
    }

    regions.forEach(([name, _code]) => {
      const isSelected = selected === name ? "selected" : ""
      html += `<option value="${this.escapeHtml(name)}" ${isSelected}>${this.escapeHtml(name)}</option>`
    })

    this.selectTarget.innerHTML = html
  }

  clearOptions() {
    const placeholder = "Select state..."
    this.selectTarget.innerHTML = `<option value="">${this.escapeHtml(placeholder)}</option>`
  }

  enableSelect() {
    this.selectTarget.disabled = false
  }

  disableSelect() {
    this.selectTarget.disabled = true
  }

  showInput() {
    if (!this.hasInputTarget) return

    this.selectTarget.disabled = true
    this.selectTarget.classList.add("hidden")
    this.selectTarget.classList.remove("block")

    this.inputTarget.disabled = false
    this.inputTarget.classList.remove("hidden")
    this.inputTarget.classList.add("block")

    if (this.selectedValue) {
      this.inputTarget.value = this.selectedValue
    }
  }

  hideInput() {
    if (!this.hasInputTarget) return

    this.inputTarget.disabled = true
    this.inputTarget.classList.add("hidden")
    this.inputTarget.classList.remove("block")
    this.inputTarget.value = ""

    this.selectTarget.classList.remove("hidden")
    this.selectTarget.classList.add("block")
  }

  findCountryElement() {
    const form = this.element.closest("form") || document
    const name = this.hasCountrySelectNameValue ? this.countrySelectNameValue : "form[country_code]"

    // Try hidden input first (HotwireCombobox renders a hidden input)
    const hiddenInput = form.querySelector(`input[type="hidden"][name="${name}"]`)
    if (hiddenInput) return hiddenInput

    // Fallback to native select
    const selectEl = form.querySelector(`select[name="${name}"]`)
    if (selectEl) return selectEl

    return null
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
